
import React from "react";
import { Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { useLanguage } from "@/components/LanguageContext";
import { useLanguagePath } from "@/utils/language";
import { Navigation } from "@/components/Navigation";
import { Testimonials } from "@/components/landing/Testimonials";
import { Footer } from "@/components/landing/Footer";
import { ChevronRight, Star } from "lucide-react";

const TestimonialsPage: React.FC = () => {
  const { language } = useLanguage();
  const { getPath } = useLanguagePath();

  const getTitle = () => {
    switch(language) {
      case 'pt': return "TrainerFOLIO - Depoimentos";
      case 'es': return "TrainerFOLIO - Testimonios";
      case 'fr': return "TrainerFOLIO - Témoignages";
      default: return "TrainerFOLIO - Testimonials";
    }
  };

  const getDescription = () => {
    switch(language) {
      case 'pt': return "Veja o que os Personal Trainers estão falando sobre a plataforma TrainerFOLIO.";
      case 'es': return "Vea lo que los Entrenadores Personales están diciendo sobre la plataforma TrainerFOLIO.";
      case 'fr': return "Voyez ce que les Personal Trainers disent de la plateforme TrainerFOLIO.";
      default: return "See what Personal Trainers are saying about the TrainerFOLIO platform.";
    }
  };

  const getHomeText = () => {
    switch(language) {
      case 'pt': return 'Início';
      case 'es': return 'Inicio';
      case 'fr': return 'Accueil';
      default: return 'Home';
    }
  };

  const getTestimonialsText = () => {
    switch(language) {
      case 'pt': return 'Depoimentos';
      case 'es': return 'Testimonios';
      case 'fr': return 'Témoignages';
      default: return 'Testimonials';
    }
  };

  const getMoreTestimonialsText = () => {
    switch(language) {
      case 'pt': return 'Mais Depoimentos';
      case 'es': return 'Más Testimonios';
      case 'fr': return 'Plus de Témoignages';
      default: return 'More Testimonials';
    }
  };

  const getClientText = () => {
    switch(language) {
      case 'pt': return 'Veja o que nossos clientes estão dizendo sobre a plataforma TrainerFOLIO.';
      case 'es': return 'Vea lo que nuestros clientes están diciendo sobre la plataforma TrainerFOLIO.';
      case 'fr': return 'Voyez ce que nos clients disent de la plateforme TrainerFOLIO.';
      default: return 'See what our clients are saying about the TrainerFOLIO platform.';
    }
  };

  const getReviewsText = () => {
    switch(language) {
      case 'pt': return 'mais de 1000 avaliações';
      case 'es': return 'más de 1000 reseñas';
      case 'fr': return 'plus de 1000 avis';
      default: return 'over 1000 reviews';
    }
  };

  const getTestimonialOne = () => {
    switch(language) {
      case 'pt': return '"TrainerFOLIO transformou completamente o meu negócio. Agora posso me concentrar em treinar meus clientes em vez de lidar com papelada e planilhas."';
      case 'es': return '"TrainerFOLIO transformó completamente mi negocio. Ahora puedo concentrarme en entrenar a mis clientes en lugar de lidiar con papeleo y hojas de cálculo."';
      case 'fr': return '"TrainerFOLIO a complètement transformé mon entreprise. Maintenant, je peux me concentrer sur l\'entraînement de mes clients au lieu de gérer des papiers et des feuilles de calcul."';
      default: return '"TrainerFOLIO completely transformed my business. Now I can focus on training my clients instead of dealing with paperwork and spreadsheets."';
    }
  };

  const getTestimonialTwo = () => {
    switch(language) {
      case 'pt': return '"A função de análise de progresso é incrível. Meus alunos adoram ver seus resultados visualizados em gráficos e isso tem aumentado muito a motivação deles."';
      case 'es': return '"La función de análisis de progreso es increíble. A mis estudiantes les encanta ver sus resultados visualizados en gráficos y esto ha aumentado enormemente su motivación."';
      case 'fr': return '"La fonction d\'analyse de progression est incroyable. Mes élèves adorent voir leurs résultats visualisés en graphiques et cela a considérablement augmenté leur motivation."';
      default: return '"The progress analysis function is amazing. My students love seeing their results visualized in graphs, and it has greatly increased their motivation."';
    }
  };

  const getLocation = (city: string) => {
    switch(language) {
      case 'pt': return `Personal Trainer, ${city}`;
      case 'es': return `Entrenador Personal, ${city}`;
      case 'fr': return `Personal Trainer, ${city}`;
      default: return `Personal Trainer, ${city}`;
    }
  };

  // Schema.org data
  const schemaData = {
    itemReviewed: {
      "@type": "SoftwareApplication",
      name: "TrainerFOLIO",
      applicationCategory: "BusinessApplication"
    },
    reviewRating: {
      "@type": "Rating",
      ratingValue: "4.9",
      bestRating: "5"
    },
    author: {
      "@type": "Organization",
      name: "TrainerFOLIO"
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <SEOHead 
        title={getTitle()}
        description={getDescription()}
        canonicalPath={getPath('/testimonials')}
        schemaType="Review"
        schemaData={schemaData}
      />

      <header>
        <Navigation />
      </header>

      <main className="flex-grow bg-gradient-to-b from-slate-50 to-white dark:from-gray-900 dark:to-gray-950">
        {/* Breadcrumbs */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 mt-20">
          <nav className="flex items-center text-sm text-slate-500 dark:text-slate-400">
            <Link to={getPath('/')} className="hover:text-flame">
              {getHomeText()}
            </Link>
            <ChevronRight className="h-4 w-4 mx-2" />
            <span className="font-medium text-slate-700 dark:text-slate-300">
              {getTestimonialsText()}
            </span>
          </nav>
        </div>

        {/* Page Header */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h1 className="text-4xl font-bold text-black dark:text-white">
            {getTestimonialsText()}
          </h1>
          <p className="mt-4 text-xl text-slate-600 dark:text-slate-400 max-w-3xl">
            {getClientText()}
          </p>
          
          <div className="flex items-center mt-4">
            <Star className="h-5 w-5 fill-yellow-500 text-yellow-500" />
            <Star className="h-5 w-5 fill-yellow-500 text-yellow-500" />
            <Star className="h-5 w-5 fill-yellow-500 text-yellow-500" />
            <Star className="h-5 w-5 fill-yellow-500 text-yellow-500" />
            <Star className="h-5 w-5 fill-yellow-500 text-yellow-500" />
            <span className="ml-2 text-slate-700 dark:text-slate-300">4.9/5 - {getReviewsText()}</span>
          </div>
        </div>

        {/* Testimonials Section */}
        <Testimonials />

        {/* Additional Testimonials */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="text-3xl font-bold text-black dark:text-white mb-8 text-center">
            {getMoreTestimonialsText()}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-md">
              <Star className="h-8 w-8 text-flame" />
              <p className="text-slate-600 dark:text-slate-300 my-4 italic">
                {getTestimonialOne()}
              </p>
              <div>
                <p className="font-semibold text-black dark:text-white">Ricardo Almeida</p>
                <p className="text-slate-500 dark:text-slate-400 text-sm">
                  {getLocation('Lisboa')}
                </p>
              </div>
            </div>
            
            <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-md">
              <Star className="h-8 w-8 text-flame" />
              <p className="text-slate-600 dark:text-slate-300 my-4 italic">
                {getTestimonialTwo()}
              </p>
              <div>
                <p className="font-semibold text-black dark:text-white">Sofia Santos</p>
                <p className="text-slate-500 dark:text-slate-400 text-sm">
                  {getLocation('Porto')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <footer>
        <Footer />
      </footer>
    </div>
  );
};

export default TestimonialsPage;
